import { action, computed, makeObservable, observable } from 'mobx'
import { Observable, Subscription, distinctUntilChanged } from 'rxjs'
import { AlertFactory } from 'alerts/Alert'
import DocumentStore from 'documents/DocumentStore'
import DocumentTypeStore from 'documents/DocumentTypeStore'
import { DocumentDetailsRootViewModel } from 'documents/views/DocumentRoot/DocumentDetailsRootViewModel'
import Member from 'iam/models/Member'
import { GetCurrentMember } from 'iam/SessionStore'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { PrefilledDraftInput } from 'jobs/creation/PrefilledDraftInput'
import JobCreationDialogViewModel from 'jobs/creation/views/JobCreationDialog/JobCreationDialogViewModel'
import JobStore from 'jobs/JobStore'
import JobStatsStore from 'jobs/job-stats/JobStatsStore'
import { JobManagementViewModelFactory } from 'jobs/views/JobManagement/JobManagementViewModel'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import { Id } from 'types/definitions'
import { tapAction, toStream } from 'utils/mobx-util'
import { JobsTabViewModel } from 'user-management/views/JobsTab/JobsTabViewModel'
import { DocumentsTabViewModel } from 'user-management/views/DocumentsTab/DocumentsTabViewModel'
import UserInfoItemListViewModel from 'user-management/views/UserInfoItemListDialog/UserInfoItemListViewModel'
import MemberStore from 'iam/MemberStore'

/**
 * List of tabs to be displayed in the client user details view.
 */
export enum ClientUserDetailsTab {
  Jobs = 'Jobs',
  Documents = 'Documents',
}

export class ClientUserDetailsViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * The currently selected user.
   */
  @observable
  member: Member = Member.none

  /**
   * The currently selected tab.
   */
  @observable
  tab: ClientUserDetailsTab = ClientUserDetailsTab.Jobs

  /**
   * Whether the user is initialized.
   */
  @observable
  isUserInitialized = false

  jobsTabVM = new JobsTabViewModel(
    () => this.member.user.publicId,
    () => [this.currentUserWorkspace],
    this.jobManagementFactory('UserDetails'),
  )

  documentsTabVM = new DocumentsTabViewModel(
    () => this.member.user.publicId,
    () => [this.member.toMembership()],
    () => [this.getCurrentMember().toMembership()],
    this.documentDetailsRootVM,
    this.documentStore,
    this.documentTypeStore,
    this.jobStore,
    this.jobSearchStore,
    this.currentMember$,
    this.getCurrentMember,
    this.filterStorageFactory,
    this.newAlert,
  )

  /**
   * Stream of changes to the user details view.
   */
  private userDetails$ = toStream(() => this.tab, true).pipe(
    distinctUntilChanged(),
    tapAction((tab) => {
      switch (tab) {
        case ClientUserDetailsTab.Jobs:
          this.jobsTabVM.activate()
          break
        case ClientUserDetailsTab.Documents:
          this.documentsTabVM.activate()
          break
      }
    }),
  )

  /**
   * Creates a new user info item list view model.
   */
  userInfoItemListVM = new UserInfoItemListViewModel()

  constructor(
    private currentMember$: Observable<Member>,
    private getCurrentMember: GetCurrentMember,
    private documentStore: DocumentStore,
    private documentTypeStore: DocumentTypeStore,
    private jobStore: JobStore,
    private jobSearchStore: JobSearchStore,
    private memberStore: MemberStore,
    private newAlert: AlertFactory,
    private canAccessClientUserManagement: () => boolean,
    private jobCreationDialogVM: JobCreationDialogViewModel,
    private jobManagementFactory: JobManagementViewModelFactory,
    private jobStatsStore: JobStatsStore,
    private documentDetailsRootVM: DocumentDetailsRootViewModel,
    private filterStorageFactory: FilterStorageFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Activates the view model.
   */
  async activate() {
    await this.initialize()
    this.subscription = this.userDetails$.subscribe()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
    this.reset()

    this.jobsTabVM.deactivate()
    this.documentsTabVM.deactivate()
  }

  /**
   * The workspace available to the current logged in user (The admin)
   */
  @computed
  get currentUserWorkspace() {
    return this.getCurrentMember().workspace
  }

  /**
   * Whether the user should be allowed access to view user details.
   */
  @computed
  get canAccessUserDetails() {
    return this.canAccessClientUserManagement()
  }

  @computed
  get canCreateJobForUser() {
    return this.getCurrentMember().hasPermission('ADMIN_JOB_CREATE')
  }

  /**
   * Set the currently selected user.
   */
  @action.bound
  setMember(member: Member) {
    this.member = member
  }

  /**
   * Set the currently selected tab.
   */
  @action.bound
  setTab(tab: ClientUserDetailsTab) {
    this.tab = tab
  }

  /**
   * Set whether the user is initialized.
   */
  @action.bound
  setIsUserInitialized(isUserInitialized: boolean) {
    this.isUserInitialized = isUserInitialized
  }

  /**
   * Initializes the view model.
   */
  async initialize() {
    // Collect the user public id from the URL. Assume a length of 36 characters.
    const userPublicId = window.location.pathname
      .split('/clients/')[1]
      .substring(0, 36)

    await this.memberStore
      .fetchByUserPublicId({
        workspace_id: Id.assert(this.currentUserWorkspace.id),
        user_public_id: Id.assert(userPublicId),
      })
      .then((member) => {
        if (member === null) {
          return Member.none
        }

        this.setMember(member)
        this.setIsUserInitialized(true)
        return member
      })
  }

  /**
   * Creates job for user.
   */
  async createJob() {
    if (!this.member.user.publicId) {
      return
    }

    this.jobCreationDialogVM.open(
      undefined,
      new PrefilledDraftInput(undefined, undefined, undefined, [], {
        publicId: this.member.user.publicId,
        workspaceIds: [Id.assert(this.currentUserWorkspace.id)],
      }),
    )
  }

  /**
   * Resets the view model.
   */
  @action.bound
  reset() {
    this.setIsUserInitialized(false)
    this.setMember(Member.none)
    this.setTab(ClientUserDetailsTab.Jobs)
  }
}
